import posthog from 'posthog-js'
import type { CaptureResult } from 'posthog-js'
import { allContent, contentPath } from './content'
import { analyticsAllowed, readConsent } from './consent'

/**
 * Client-side analytics: PostHog (primary) and Google Tag Manager.
 *
 * The site is prerendered (see dev/ssr.md), so nothing here may run during
 * render: `initAnalytics()` is called once from `entry-client.tsx` after
 * hydration and bails out unless a real browser environment is present. The
 * pure helpers (`classifyCta`, `sectionLabelFor`, `contentMetaForPath`) are
 * exported for unit tests.
 *
 * GA4 is NOT loaded directly: the GTM container carries the Google tag
 * (G-WJ12Y0LCFY), and it also covers the docs under langwatch.ai/docs.
 * Loading gtag here as well double-counts every pageview in GA4.
 *
 * Configuration comes from Vite env vars with committed defaults (the PostHog
 * client key is public by design, same as the docs site):
 * - VITE_POSTHOG_KEY       project API key (default: the LangWatch EU project)
 * - VITE_POSTHOG_API_HOST  ingestion host; a path like `/ingest` is resolved
 *                          against the page origin and served by the
 *                          first-party proxy in `functions/ingest/[[path]].js`
 *                          (default: https://eu.i.posthog.com, direct)
 * - VITE_POSTHOG_UI_HOST   PostHog app host, needed when proxying
 * - VITE_GTM_ID            Google Tag Manager container id (default: the
 *                          LangWatch container; empty string disables it).
 *                          Loads deferred and only on the production
 *                          hostnames, see `gtmShouldLoad`.
 * - VITE_ENVIRONMENT       overrides the `environment` super property
 *                          (defaults to the Vite build mode)
 *
 * The deploy workflows (.github/workflows/deploy-*.yml) set the proxy host and
 * the per-environment values at build time.
 *
 * Cross-domain journey stitching: we keep posthog-js defaults for persistence
 * and `cross_subdomain_cookie`, so the distinct_id cookie is written to
 * `.langwatch.ai` and app.langwatch.ai (same project key) picks up the same
 * visitor when it calls `identify()` after signup/login.
 */

const env = import.meta.env

export const POSTHOG_KEY =
  env.VITE_POSTHOG_KEY || 'phc_oOlj3H19T2JlGbFXmrGrjSLbDPDNyPKYdIFaTdrkXOY'
export const POSTHOG_API_HOST = env.VITE_POSTHOG_API_HOST || 'https://eu.i.posthog.com'
export const POSTHOG_UI_HOST = env.VITE_POSTHOG_UI_HOST || 'https://eu.posthog.com'
// Google Tag Manager container. Nullish so an explicit empty string disables it.
export const GTM_ID = env.VITE_GTM_ID ?? 'GTM-KJ4S6Z9C'
export const ANALYTICS_ENVIRONMENT = env.VITE_ENVIRONMENT || env.MODE

let initialized = false

/**
 * Idempotent client-only entry point, called from entry-client after hydration
 * and again by the cookie banner the moment a visitor accepts.
 *
 * Consent gates everything: with no stored choice, or a declined one, this is a
 * no-op and neither PostHog nor GTM is ever loaded. That also means the
 * `?gtm=force` test override only applies once consent is given.
 */
export function initAnalytics(): void {
  if (initialized) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (!analyticsAllowed(readConsent())) return
  initialized = true

  initPostHog()
  initGtm()

  // Wire flag subscriptions made by components that mounted before init.
  pendingFlagWires.splice(0).forEach((wire) => wire())

  // Capture phase so CTA clicks are seen even when a handler stops propagation.
  document.addEventListener('click', onDocumentClick, true)
}

/** Capture a custom event (no-op before init, e.g. during prerender or tests). */
export function trackEvent(name: string, properties?: Record<string, unknown>): void {
  if (!initialized) return
  posthog.capture(name, properties)
}

/* ── Feature flags (experiments) ─────────────────────────────────── */

/** Flag subscriptions made before `initAnalytics` ran; wired up on init. */
const pendingFlagWires: Array<() => void> = []

/**
 * Subscribe to a multivariate feature flag's variant for the current visitor.
 * Safe to call from component effects at any time: components mount before
 * `initAnalytics` runs (it is dynamically imported after hydration), so early
 * subscriptions are queued and wired once PostHog initializes. The callback
 * fires when flags load (and again on any reload); it never fires during
 * prerender or when PostHog is unavailable, so callers keep their default.
 *
 * Reading the variant with `getFeatureFlag` (rather than peeking at cached
 * payloads) also reports exposure, which is what enrolls the visitor in the
 * experiment's cohort.
 */
export function subscribeFeatureFlagVariant(
  flag: string,
  cb: (variant: string | undefined) => void,
): () => void {
  let unsub: (() => void) | null = null
  let disposed = false
  const wire = () => {
    if (disposed) return
    unsub = posthog.onFeatureFlags(() => {
      const value = posthog.getFeatureFlag(flag)
      cb(typeof value === 'string' ? value : undefined)
    })
  }
  if (initialized) wire()
  else pendingFlagWires.push(wire)
  return () => {
    disposed = true
    unsub?.()
  }
}

/* ── PostHog ─────────────────────────────────────────────────────── */

function initPostHog(): void {
  // A path-style host (the `/ingest` proxy) is resolved against the page
  // origin, so the same build works on production and preview domains.
  const apiHost = POSTHOG_API_HOST.startsWith('/')
    ? window.location.origin + POSTHOG_API_HOST
    : POSTHOG_API_HOST

  posthog.init(POSTHOG_KEY, {
    api_host: apiHost,
    ui_host: POSTHOG_UI_HOST,
    // Fires $pageview on load and on history pushState/replaceState/popstate.
    // The site is an MPA today (every nav is a full load), so this is mostly
    // the initial pageview, but it keeps coverage if client-side nav appears.
    capture_pageview: 'history_change',
    // $pageleave carries scroll depth ($max_scroll_percentage etc.) per page.
    capture_pageleave: true,
    autocapture: true,
    // Recording is a separate product decision; off for now.
    disable_session_recording: true,
    // Persistence and cross_subdomain_cookie stay at their defaults on purpose:
    // the cookie lands on `.langwatch.ai`, which is what lets app.langwatch.ai
    // (same project) stitch the anonymous website visit to the signed-up user.
    before_send: enrichWithContentMeta,
  })

  // Super property on every event so dev/ci/preview traffic is filterable
  // from production in analyses.
  posthog.register({ environment: ANALYTICS_ENVIRONMENT })

  // The snippet loader exposes the client globally; the npm bundle does not.
  // Expose it for the PostHog toolbar and for forcing experiment variants in
  // QA (posthog.featureFlags.overrideFeatureFlags).
  ;(window as unknown as { posthog?: typeof posthog }).posthog = posthog
}

/**
 * before_send hook: on blog/changelog/customer-story routes, stamp the content
 * metadata onto every event ($pageview, $pageleave, autocapture, cta_click)
 * so per-content analysis needs no joins.
 */
function enrichWithContentMeta(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event
  const meta = contentMetaForPath(window.location.pathname)
  if (meta) event.properties = { ...event.properties, ...meta }
  return event
}

let contentByPath: Map<string, Record<string, string>> | null = null

/** Content metadata (slug, category, author, publish date) for a pathname, or null. */
export function contentMetaForPath(pathname: string): Record<string, string> | null {
  if (!contentByPath) {
    contentByPath = new Map()
    for (const entry of allContent()) {
      const props: Record<string, string> = {
        content_collection: entry.collection,
        content_slug: entry.slug,
        content_title: entry.meta.title,
      }
      if (entry.meta.category) props.content_category = entry.meta.category
      if (entry.meta.author) props.content_author = entry.meta.author
      if (entry.meta.date) props.content_published_at = entry.meta.date
      contentByPath.set(normalizePath(contentPath(entry)), props)
    }
  }
  return contentByPath.get(normalizePath(pathname)) ?? null
}

/** Decode percent-encoding (slugs can contain non-ASCII) and drop trailing slashes. */
function normalizePath(pathname: string): string {
  let p = pathname
  try {
    p = decodeURIComponent(p)
  } catch {
    // keep the raw path when it is not valid percent-encoding
  }
  p = p.replace(/\/+$/, '')
  return p === '' ? '/' : p
}

/* ── CTA click tracking ──────────────────────────────────────────── */

export type CtaType = 'app' | 'demo' | 'booking' | 'github' | 'docs' | 'pricing'

/**
 * Classify a link destination as a CTA. Covers the whole site generically:
 * links to the app, the demo page, calendar booking, GitHub, docs, and
 * pricing. Returns null for everything else.
 */
export function classifyCta(href: string, pageOrigin: string): CtaType | null {
  let url: URL
  try {
    url = new URL(href, pageOrigin)
  } catch {
    return null
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null

  const host = url.hostname
  if (host === 'app.langwatch.ai') return 'app'
  if (host === 'cal.com' || host.endsWith('.cal.com')) return 'booking'
  if (host === 'calendly.com' || host.endsWith('.calendly.com')) return 'booking'
  if (host === 'github.com' || host.endsWith('.github.com')) return 'github'
  if (host === 'docs.langwatch.ai') return 'docs'

  const sameSite =
    url.origin === pageOrigin || host === 'langwatch.ai' || host === 'www.langwatch.ai'
  if (sameSite) {
    const path = normalizePath(url.pathname)
    if (path === '/get-a-demo') return 'demo'
    if (path === '/pricing') return 'pricing'
  }
  return null
}

/**
 * A human-readable label for the container a CTA sits in. Precedence:
 * an explicit `data-analytics-section` ancestor (use it only where the
 * generic derivation is ambiguous), footer/nav landmarks, the nearest
 * ancestor with an id, then the nearest section's heading.
 */
export function sectionLabelFor(el: Element): string {
  const tagged = el.closest('[data-analytics-section]')
  if (tagged) return tagged.getAttribute('data-analytics-section') || 'section'
  if (el.closest('footer')) return 'footer'
  if (el.closest('header, nav')) return 'nav'
  const scope = el.closest('section, [id]')
  if (!scope) return 'page'
  if (scope.id) return scope.id
  const heading = scope.querySelector('h1, h2, h3')
  const label = heading?.textContent?.replace(/\s+/g, ' ').trim()
  return label ? `section: ${label.slice(0, 80)}` : 'section'
}

function ctaText(el: Element): string {
  const text = (el.textContent || '').replace(/\s+/g, ' ').trim()
  if (text) return text.slice(0, 120)
  const fallback =
    el.getAttribute('aria-label') ||
    el.getAttribute('title') ||
    el.querySelector('img[alt]')?.getAttribute('alt') ||
    ''
  return fallback.replace(/\s+/g, ' ').trim().slice(0, 120)
}

function onDocumentClick(event: MouseEvent): void {
  const target = event.target
  if (!(target instanceof Element)) return
  const anchor = target.closest('a[href]')
  if (!anchor) return
  const href = anchor.getAttribute('href')
  if (!href) return

  const type = classifyCta(href, window.location.origin)
  if (!type) return

  const destination = new URL(href, window.location.origin).href
  const explicitId = anchor.getAttribute('data-cta')
  posthog.capture(
    'cta_click',
    {
      cta_text: ctaText(anchor),
      cta_type: type,
      cta_destination: destination,
      section: sectionLabelFor(anchor),
      page_path: window.location.pathname,
      ...(explicitId ? { cta_id: explicitId } : {}),
    },
    // Outbound CTAs unload the page; beacons survive navigation.
    { transport: 'sendBeacon' },
  )
}

/* ── Google Tag Manager ──────────────────────────────────────────── */

type DataLayerWindow = Window & { dataLayer?: unknown[] }

/** Production hostnames the GTM container is allowed to load on. */
const TAG_HOSTNAMES = ['langwatch.ai', 'www.langwatch.ai']

/**
 * Whether the GTM container may load. Unlike PostHog (environment-tagged and
 * bot-filtered client-side), the container cannot keep non-production traffic
 * out of its tags, and it pulls third-party requests and cookies into every
 * page (which also fails the CI Lighthouse best-practices budget), so it only
 * loads on the production hostnames (or with `?gtm=force`).
 */
export function gtmShouldLoad(location: { hostname: string; search: string }): boolean {
  if (new URLSearchParams(location.search).get('gtm') === 'force') return true
  return TAG_HOSTNAMES.includes(location.hostname)
}

/**
 * Load the GTM container, deferred after hydration like the other tags. The
 * site is an MPA (every navigation is a full page load), so gtm.js
 * re-initialises per page and no SPA history hook is needed; individual tags
 * fire from their triggers configured inside the container in the GTM UI.
 *
 * The container owns GA4 (its Google tag), the ad pixels, and reo.dev. Do not
 * load gtag directly here as well: two page_view sources on the same GA4
 * property double-count every visit.
 */
function initGtm(): void {
  if (!GTM_ID || !gtmShouldLoad(window.location)) return
  const w = window as DataLayerWindow
  w.dataLayer = w.dataLayer || []
  w.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`
  document.head.appendChild(script)
}
